import React from 'react';

import { BaseApiError } from '@lumapps/base-api';
import { customerIdSelector } from '@lumapps/customer/ducks/selectors';
import { useSelector } from '@lumapps/redux/react';
import { Route, useRouter } from '@lumapps/router';
import { DEFAULT_AVATAR } from '@lumapps/user/constants';
import { getUserProfilePictureUrl } from '@lumapps/user/utils/getUserProfilePicture';

import { getVideo } from '../../api';
import type { GetVideoResult, ThumbnailSizes, Video } from '../../api/types';
import { THUMBNAIL_XLARGE } from '../../constants';

export const VIDEO_PAGE_FIELDS: (keyof Video)[] = [
    'asset',
    'chapters',
    'createdAt',
    'createdByDetails',
    'createdById',
    'publishedAt',
    'publicationStatus',
    'description',
    'id',
    'isDownloadable',
    'tagz',
    'title',
    'thumbnail',
    'transcript',
    'views',
    'editableByGroupIds',
    'editableByGroupDetails',
    'currentUserReactions',
    'reactionCounts',
    'commentCount',
    'cuepoints',
    'chapters',
    'subtitles',
    'manifestDynamicUrl',
    'analyticsUrl',
    'chaptersVTT',
];

export const WIDGET_PLAY_SETTINGS_FIELDS: (keyof Video)[] = ['id', 'thumbnail', 'title'];

export const HERO_FIELDS: (keyof Video)[] = ['manifestDynamicUrl', 'asset'];

export const CLIENT_COMPUTED_PLAY_FIELDS: (keyof Video)[] = [
    'createdAt',
    'createdByDetails',
    'createdById',
    'publishedAt',
    'publicationStatus',
    'description',
    'id',
    'thumbnail',
    'title',
    'views',
    'currentUserReactions',
    'reactionCounts',
    'tagz',
    'isDownloadable',
    'commentCount',
];

export interface UseFetchUniqueVideoParams {
    videoId: Video['id'] | undefined;
    notFoundRoute?: Route;
    fields?: (keyof Video)[];
}

export const useFetchUniqueVideo = (
    { videoId, notFoundRoute, fields }: UseFetchUniqueVideoParams,
    {
        enabled = true,
        thumbnailSizes = THUMBNAIL_XLARGE,
    }: {
        enabled?: boolean;
        /** The thumbnail sizes to retrieve from getVideo()
         * @default THUMBNAIL_XLARGE
         */
        thumbnailSizes?: ThumbnailSizes;
    } = {},
) => {
    const { redirect } = useRouter();
    const [videoLoading, setVideoLoading] = React.useState(false);
    const [video, setVideo] = React.useState<GetVideoResult>();
    const [videoError, setVideoError] = React.useState(false);
    const [videoErrorCode, setVideoErrorCode] = React.useState<number | undefined>();
    const customerId = useSelector(customerIdSelector);

    const fetchVideo = React.useCallback(async () => {
        if (videoId) {
            setVideoLoading(true);
            try {
                const fetchedVideo = await getVideo(videoId, fields, { thumbnailSizes });

                if (fetchedVideo) {
                    const profilePictureUrl = fetchedVideo.createdByDetails?.id
                        ? // eslint-disable-next-line deprecation/deprecation
                          getUserProfilePictureUrl({
                              id: fetchedVideo.createdByDetails.id,
                              customer: customerId,
                          })
                        : DEFAULT_AVATAR;

                    const formattedVideo = {
                        ...fetchedVideo,
                        createdByDetails: fetchedVideo.createdByDetails
                            ? {
                                  ...fetchedVideo.createdByDetails,
                                  profilePictureUrl,
                              }
                            : undefined,
                    } as Video;

                    setVideo(formattedVideo);
                }
                setVideoErrorCode(undefined);
            } catch (error) {
                setVideo(undefined);
                setVideoError(true);
                if (error instanceof BaseApiError) {
                    setVideoErrorCode(error?.status);
                }
                if (notFoundRoute) {
                    redirect(notFoundRoute);
                }
            }
        } else {
            setVideo(undefined);
        }
        setVideoLoading(false);
    }, [customerId, fields, notFoundRoute, redirect, thumbnailSizes, videoId]);

    React.useEffect(() => {
        if (enabled) {
            fetchVideo();
        }
    }, [enabled, fetchVideo]);

    return {
        videoLoading,
        video,
        videoError,
        videoErrorCode,
        fetchVideo,
    };
};
